import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { compressImage, generateId } from '@/lib/app-utils';
import { toast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { dailyReportsAPI } from '@/services/dailyReports';
import { Loader } from 'lucide-react';

const DailyReportsPage = () => {
  const { user } = useAuth();
  const [reportText, setReportText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]); // تغيير من images لـ selectedFiles
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await dailyReportsAPI.list();
      if (response.success) {
        setReports(response.reports || []);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast({ 
        title: "Error", 
        description: "Failed to load reports",
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = async (e) => {
    if (selectedFiles.length >= 4) {
      toast({ 
        title: "Maximum files reached", 
        description: "You can only upload up to 4 images",
        variant: "destructive" 
      });
      return;
    }

    const files = Array.from(e.target.files);
    const remainingSlots = 4 - selectedFiles.length;
    const filesToAdd = files.slice(0, remainingSlots);

    for (const file of filesToAdd) {
      if (file.size > 5 * 1024 * 1024) {
        toast({ 
          title: "Image too large", 
          description: `${file.name} is larger than 5MB`,
          variant: "destructive" 
        });
        continue;
      }

      // إنشاء preview للصورة
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedFiles(prev => [...prev, {
          file: file,
          preview: e.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    }

    // Clear input
    e.target.value = '';
  };

  const removeImage = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  if (!reportText.trim()) {
    toast({ 
      title: "Error", 
      description: "Please enter report text",
      variant: "destructive" 
    });
    return;
  }

  try {
    setSubmitting(true);
    
    // ✅ إنشاء FormData لإرسال الملفات
    const formData = new FormData();
    formData.append('userName', user?.name || 'Unknown User');
    formData.append('userId', user?.id || '');
    formData.append('projectId', '');
    formData.append('date', new Date().toISOString().split('T')[0]);
    formData.append('workDone', reportText);
    formData.append('title', 'Daily Report');

    // ✅ إضافة الصور بالطريقة الصحيحة
    selectedFiles.forEach((fileObj, index) => {
      formData.append(`images[${index}]`, fileObj.file);
    });

    // ✅ طباعة البيانات للتأكد
    console.log('FormData contents:');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + pair[1]);
    }

    const response = await dailyReportsAPI.create(formData);
    
    if (response.success) {
      await fetchReports();
      setReportText('');
      setSelectedFiles([]);
      toast({ 
        title: "Success", 
        description: "Report submitted successfully" 
      });
    }
  } catch (error) {
    console.error('Failed to submit report:', error);
    toast({ 
      title: "Error", 
      description: error.message || "Failed to submit report",
      variant: "destructive" 
    });
  } finally {
    setSubmitting(false);
  }
};


  const handleAdminNote = async (reportId, note) => {
    try {
      const response = await dailyReportsAPI.updateAdminNote(reportId, note);
      
      if (response.success) {
        setReports(prev => prev.map(r => 
          r.id === reportId ? { ...r, admin_notes: note } : r
        ));
        toast({ 
          title: "Success", 
          description: "Note updated successfully" 
        });
      }
    } catch (error) {
      console.error('Failed to update admin note:', error);
      toast({ 
        title: "Error", 
        description: "Failed to update note",
        variant: "destructive" 
      });
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;

    try {
      const response = await dailyReportsAPI.delete(reportId);
      
      if (response.success) {
        setReports(reports.filter(r => r.id !== reportId));
        toast({ 
          title: "Success", 
          description: "Report deleted successfully" 
        });
      }
    } catch (error) {
      console.error('Failed to delete report:', error);
      toast({ 
        title: "Error", 
        description: "Failed to delete report",
        variant: "destructive" 
      });
    }
  };

  const filteredReports = (user.role === 'Admin' || user.role === 'Manager') 
    ? reports 
    : reports.filter(r => r.created_by === user.id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Daily Reports - Stone Beam</title></Helmet>
      <div className="space-y-6 max-w-4xl mx-auto p-4">
        <h1 className="text-3xl font-bold text-slate-900">Daily Reports</h1>

        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4 text-lg">Submit New Report</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Textarea 
                placeholder="What did you work on today?" 
                value={reportText} 
                onChange={e => setReportText(e.target.value)}
                rows={4}
                className="resize-none"
                required
              />
              
              <div className="space-y-2">
                <div className="flex gap-4 items-center">
                  <Input 
                    type="file" 
                    accept="image/*" 
                    multiple
                    onChange={handleImageSelect} 
                    disabled={selectedFiles.length >= 4} 
                    className="w-64" 
                  />
                  <span className="text-sm text-slate-500">
                    {selectedFiles.length}/4 images
                  </span>
                </div>
                
                {selectedFiles.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {selectedFiles.map((fileObj, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={fileObj.preview} 
                          alt={`Preview ${idx + 1}`}
                          className="w-20 h-20 object-cover rounded border-2 border-slate-200" 
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                        <span className="text-xs text-slate-500 block mt-1 truncate w-20">
                          {fileObj.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                className="bg-orange-600 hover:bg-orange-700" 
                disabled={submitting || !reportText.trim()}
              >
                {submitting ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Report'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-900">
            Recent Reports ({filteredReports.length})
          </h2>
          
          {filteredReports.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-slate-500">
                No reports yet. Submit your first report above!
              </CardContent>
            </Card>
          ) : (
            filteredReports.map(report => {
              // معالجة الصور - دعم كلا النظامين القديم والجديد
              let reportImages = [];
              
              // النظام الجديد (photo_url)
              if (report.photo_url) {
                reportImages.push(report.photo_url);
              }
              
              // النظام القديم (images)
              if (report.images) {
                const oldImages = report.images.split(',')
                  .map(img => img.trim())
                  .filter(Boolean);
                reportImages = [...reportImages, ...oldImages];
              }
              
              return (
                <Card key={report.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-bold text-slate-900">
                          {report.created_by || report.userName || 'Unknown User'}
                        </h4>
                        <p className="text-xs text-slate-500">
                          {new Date(report.created_at || report.date).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {report.projectName && (
                          <p className="text-xs text-blue-600 mt-1">
                            Project: {report.projectName}
                            {report.companyName && ` (${report.companyName})`}
                          </p>
                        )}
                      </div>
                      {(user.role === 'Admin' || report.created_by === user.id) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(report.id)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      )}
                    </div>

                    <p className="text-slate-700 whitespace-pre-wrap mb-4 leading-relaxed">
                      {report.work_done || report.content}
                    </p>

                    {reportImages.length > 0 && (
                      <div className="flex gap-3 mb-4 overflow-x-auto pb-2">
                        {reportImages.map((img, i) => (
                          <a
                            key={i}
                            href={img}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0"
                          >
                            <img 
                              src={img} 
                              alt={`Report image ${i + 1}`}
                              className="h-32 w-auto rounded-lg border-2 border-slate-200 hover:border-orange-500 transition-colors cursor-pointer object-cover"
                              onError={(e) => {
                                e.target.src = '/placeholder-image.png';
                                e.target.alt = 'Image failed to load';
                              }}
                            />
                          </a>
                        ))}
                      </div>
                    )}

                    {(user.role === 'Admin' || user.role === 'Manager') && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Admin Note
                        </label>
                        <Input 
                          placeholder="Add a note for this report..." 
                          defaultValue={report.admin_notes || ''}
                          onBlur={e => {
                            if (e.target.value !== (report.admin_notes || '')) {
                              handleAdminNote(report.id, e.target.value);
                            }
                          }}
                          className="bg-yellow-50 border-yellow-200 focus:border-yellow-400"
                        />
                      </div>
                    )}

                    {report.admin_notes && user.role === 'Employee' && (
                      <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-md">
                        <p className="text-sm font-medium text-yellow-800 mb-1">
                          Admin Note:
                        </p>
                        <p className="text-sm text-yellow-700">
                          {report.admin_notes}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default DailyReportsPage;
